<template>
  <div>
    <h3>Graduate: {{ graduate.name }}</h3>

    <div class="card">
      <div class="card-header">
        <a class data-toggle="collapse" href="#collapseDirections" role="button" aria-expanded="false" aria-controls="collapseDirections">
          <div class="row">
            <div class="col">
              <h3 class="card-header-title">
                <font-awesome :icon="['far', 'info-circle']" class="text-muted mr-2"/> Directions
              </h3>
            </div>

            <div class="col-auto">
              <span class="collapse-marker">
                Hide / Show
                <font-awesome :icon="['fas', 'chevron-down']"/>
              </span>
            </div>
          </div><!-- /.row -->
        </a>
      </div>

      <div class="collapse show" id="collapseDirections">
        <div class="card-body card-body-info">
          <p class="mb-0">Please select the statement that is most appropriate and provide commentary about the graduate’s performance and development throughout the placement.</p>
        </div>
      </div><!-- /.collapse -->
    </div><!-- /.card -->

    <div class="card">
      <ul class="list-data no-links no-hover">
        <li>
          <div class="form-check abc-checkbox abc-checkbox-success mb-3">
            <input
              v-model="stateAssessment"
              v-validate.disable="'required'"
              class="form-check-input"
              type="radio"
              name="radioOverallAssessment"
              data-vv-as="Overall Assessment"
              id="radioOverallExceeding"
              value="exceeding">
            <label class="form-check-label strong" for="radioOverallExceeding">Exceeding</label>
          </div>

          <p class="mb-2">Overall contribution exceeds expectations for the role.</p>

          <ul>
            <li>Graduate regularly accomplishes more than what is expected and does so with a lower level of guidance than expected.</li>
            <li>Behaviours and use of capabilities consistently exceeds expectations.</li>
          </ul>
        </li>

        <li>
          <div class="form-check abc-checkbox abc-checkbox-success mb-3">
            <input
              v-model="stateAssessment"
              class="form-check-input"
              type="radio"
              name="radioOverallAssessment"
              id="radioOverallAchieving"
              value="achieving">
            <label class="form-check-label strong" for="radioOverallAchieving">Achieving</label>
          </div>

          <p class="mb-2">Overall contribution consistently meets expectations for the role.</p>

          <ul>
            <li>Graduate is seen as being a solid and reliable contributor who can be counted on to do what needs to be done.</li>
            <li>Behaviours and use of capabilities fully meets expectations.</li>
          </ul>
        </li>

        <li>
          <div class="form-check abc-checkbox abc-checkbox-success mb-3">
            <input
              v-model="stateAssessment"
              class="form-check-input"
              type="radio"
              name="radioOverallAssessment"
              id="radioOverallProgressing"
              value="progressing">
            <label class="form-check-label strong" for="radioOverallProgressing">Progressing</label>
          </div>

          <p class="mb-2">Some development is required to meet expectations for the role.</p>

          <ul>
            <li>Graduate is progressing but is not meeting all expectations for the role.</li>
            <li>Some improvement and additional support is needed to fully meet expectations.</li>
            <li>Some capability development is required to consistently meet established expectations.</li>
          </ul>
        </li>

        <li>
          <div class="form-check abc-checkbox abc-checkbox-success mb-3">
            <input
              v-model="stateAssessment"
              class="form-check-input"
              type="radio"
              name="radioOverallAssessment"
              id="radioOverallUnsatisfactory"
              value="unsatisfactory">
            <label class="form-check-label strong" for="radioOverallUnsatisfactory">Unsatisfactory</label>
          </div>

          <p class="mb-2">Overall and continued contribution is well below expectations for the role.</p>

          <ul>
            <li>Graduate has been provided with ongoing support and regular feedback on the capabilities and requirements for the role, however, unsatisfactory performance is unable to be resolved during this placement.</li>
            <li>Requires a higher level of supervision and direction than is appropriate for the role.</li>
          </ul>
        </li>
      </ul><!-- /.list-data -->
    </div><!-- /.card -->

    <div class="card">
      <div class="card-body">
        <div class="form-group mb-0">
          <p class="control-label">Graduate Overall Placement Comments</p>
          <div class="mb-0 quill-html">
            <div v-html="placement.end_component.graduate_comments" />
          </div>
        </div><!-- /.form-group -->
      </div>
    </div>

    <div class="card">
      <div class="card-header">
        <strong>Values Alignment</strong>
      </div>

      <div class="card-body">
        <label class="sr-only">Input Overall Comments</label>
        <quill-wrapper style="min-height: 150px;" :config="getQuillConfig(
          'Provide an indication of the graduate’s values at work. Consider how they have demonstrated the values of the public service (integrity, trust, service, accountability), your agency, and their own motivations for their work.'
          , this.stateValuesAlignment)"
          @change="setValuesAlignment($event)"
          />
      </div>
    </div><!-- /.card -->

    <div class="card">
      <div class="card-header">
        <strong>Conduct Review</strong>
      </div>

      <div class="card-body">
        <label class="sr-only">Input Overall Comments</label>
        <quill-wrapper style="min-height: 150px;"  :config="getQuillConfig(
          'Describe the graduate’s conduct in their placement. Outline any issues that arose and how they were resolved. If there are any outstanding conduct issues, explain them here. You may consider the graduate’s alignment to your own agency’s code of conduct, or the Code of Ethics and Conduct for NSW Government Sector Employees.'
          ,  this.stateConductReview)"
          @change="setConductReview($event)"
          />
      </div>
    </div><!-- /.card -->

    <div class="card">
      <div class="card-header">
        <strong>Overall Placement Commentary</strong>
      </div>

      <div class="card-body">
        <label class="sr-only">Input Overall Comments</label>
        <quill-wrapper :config="getQuillConfig('Add comments...', this.stateManagerComments)" @change="setManagerComments($event)" />
      </div>
    </div><!-- /.card -->

    <div class="alert alert-danger" v-if="errors && errors.items.length > 0">
      <p v-for="(error, index) in errors.items" :key="`error-${index}`">{{ error.msg }}</p>
    </div>

    <div class="save-actions">
      <div class="text-right">
        <ladda-button :click="discardAndExit" class="btn btn-link btn-discard mr-2">Discard &amp; Exit</ladda-button>
        <ladda-button :click="handleSaveAndExit" class="btn btn-outline-dark mr-2">Save &amp; Exit</ladda-button>
        <ladda-button :click="handleNext" :validate="true" class="btn btn-success">Next</ladda-button>
      </div>
    </div><!-- /.save-actions -->
  </div>
</template>

<script>
import { graduateComputed, placementComputed, placementMethods } from 'vx/helpers'
import { alert } from 'utils'
export default {
  computed: {
    ...graduateComputed,
    ...placementComputed,

    quillConfig () {
      return {
        options: { placeholder: 'Add comments...' },
        value: this.stateManagerComments
      }
    },

    stateValuesAlignment: {
      get () {
        return this.placement.end_component.values_alignment
      },

      set (value) {
        this.setValuesAlignment(value)
      }
    },

    stateConductReview: {
      get () {
        return this.placement.end_component.conduct_review
      },

      set (value) {
        this.setConductReview(value)
      }
    },

    stateManagerComments: {
      get () {
        return this.placement.end_component.manager_comments
      },

      set (value) {
        this.setManagerComments(value)
      }
    },

    stateAssessment: {
      get () {
        return this.placement.end_component.assessment
      },

      set (value) {
        this.setPlacementProperty({ property: 'end_component.assessment', value })
      }
    }
  },

  methods: {
    ...placementMethods,

    setManagerComments (value) {
      this.setPlacementProperty({ property: 'end_component.manager_comments', value })
    },

    setValuesAlignment (value) {
      this.setPlacementProperty({ property: 'end_component.values_alignment', value })
    },

    setConductReview (value) {
      this.setPlacementProperty({ property: 'end_component.conduct_review', value })
    },

    getQuillConfig (placeholderText, value) {
      return {
        options: { placeholder: placeholderText },
        // value: this.stateManagerComments
        value: value
      }
    },

    async discardAndExit () {
      const confirm = await alert.confirm({
        text: 'Are you sure you want to leave? Any unsaved changes will be lost.'
      })

      if (confirm) {
        this.$router.push({ name: 'graduates.placements.single', params: { placementId: this.placement.id, graduateId: this.placement.graduate_id } })
      }
    },

    async handleNext () {
      let payload = {
        assessment: this.placement.end_component.assessment,
        manager_comments: this.placement.end_component.manager_comments,
        values_alignment: this.placement.end_component.values_alignment,
        conduct_review: this.placement.end_component.conduct_review,
        draft: false
      }

      try {
        await this.storeOverallAssessment({ placement: this.placement.id, graduate: this.placement.graduate_id, payload })

        this.setPlacementProperty({ property: 'end_component.overall_assessment_completed', value: true })
        this.$router.push({ name: 'placements.single.end.manager.review', params: { placementId: this.placement.id, graduateId: this.placement.graduate_id } })
      } catch (error) {
        if (error.response && error.response.status === 422 && error.response.data.errors) {
          for (const messages of Object.values(error.response.data.errors)) {
            messages.forEach(message => {
              this.$noty.error(message)
            })
          }
        } else {
          this.$noty.error('Unable to save overall assessment details')
        }
      }
    },

    async handleSaveAndExit () {
      let payload = {
        assessment: this.placement.end_component.assessment,
        manager_comments: this.placement.end_component.manager_comments,
        values_alignment: this.placement.end_component.values_alignment,
        conduct_review: this.placement.end_component.conduct_review,
        draft: true
      }

      await this.storeOverallAssessment({ placement: this.placement.id, graduate: this.placement.graduate_id, payload })

      this.$noty.success('Capabilities Saved')
      this.$router.push({ name: 'graduates.placements.single', params: { placementId: this.placement.id, graduateId: this.placement.graduate_id } })
      this.$validator.reset()
    }
  }
}
</script>
